import { Callout } from "../components/Callout";
import { Card } from "../components/Card";
import { Skeleton } from "../components/Skeleton";
import { VisuallyHidden } from "../components/VisuallyHidden";
import { describePlacementSource } from "../lib/placement-source";
import { formatDisplayDateLong, formatInstantDate, formatMoney } from "../lib/format";
import styles from "./HistoryPanel.module.css";
import type { LifetimeClicks, SellerPlacement, SellerPlacementHistory } from "../api/types";

/**
 * What a founder's placements actually did (canon APP-006).
 *
 * Canon § 19 makes this the measurement V0 exists to reach: a founder who spends promotional
 * credit and cannot see what it bought has no reason to want to pay for the next placement.
 * Everything on this panel therefore answers one question per row — *what ran, why it was on
 * the board, what it cost and what came of it* — and nothing here tries to answer more.
 *
 * **This is a record, not a dashboard.** Canon § 18 rules complex dashboards and charts out
 * of V0, so there is no graph, no trend and no comparison. The rows are read in the order
 * core sent them (newest display day first, then rank, with the rank-less Honorable Mentions
 * after the Top 10 of the same day); this app does not re-sort them, because that order is
 * part of the contract and a second opinion here could only disagree with it.
 *
 * Three things a founder can otherwise get wrong are **stated**, never implied:
 *
 *   1. a row outside the Top 10 has **no position** — not a zero, not a dash. Canon § 10
 *      puts an Honorable Mention outside the standings, and a placeholder in a rank column
 *      is how a founder comes to believe they were ranked last rather than not ranked;
 *   2. a rollover and a founding placement cost **nothing additional** (canon §§ 6, 9), so
 *      neither is presented with an amount that could read as a second charge;
 *   3. the figure is **qualified outbound clicks** — the same count the weekly and monthly
 *      Top lists rank on (canon § 11) — and canon § 1 promises no sales, leads or impression
 *      count. Core's own sentence saying so travels with the number and is shown verbatim.
 *
 * Every date is a UTC calendar date and is labelled as one. Canon § 8 fixes Dealbid's day to
 * UTC, and a panel that rendered these in the browser's timezone would show a founder the
 * wrong day for their own placement — the day they would then quote back to Dealbid.
 *
 * ## The lifetime figures (`APP-021`, canon decision `D-018`)
 *
 * `D-018` gives the homepage's Founding Deals wall an outbound link and refuses it any effect
 * on canon § 11's weekly and monthly lists. Recording those clicks so a founder can see them
 * is the whole reason the decision keeps them at all, which makes this panel the point of it.
 *
 * They are **two numbers and never their sum**, here and everywhere. A single merged total
 * beside a list a founder is trying to climb reads as counting toward it, and half of it
 * counts toward nothing Dealbid publishes. Each half therefore says what it counts for, in
 * words, on the same line as the figure.
 *
 * The block renders **even when there are no placements**, which is not an edge case: a wall
 * click lands on days a deal holds no position, and once the marketplace is past cold start
 * that is most days. A founder whose deal has never been on the board can still have visits,
 * and the panel that exists to show them what their deal did must not answer "nothing" when
 * the answer is a number.
 */

export function HistorySkeleton() {
  return (
    <Card className={styles.panel}>
      <Skeleton width="12rem" height="2rem" />
      <Skeleton width="70%" height="1rem" />
      <Skeleton width="100%" height="4rem" />
    </Card>
  );
}

export interface HistoryPanelProps {
  history: SellerPlacementHistory;
}

export function HistoryPanel({ history }: HistoryPanelProps) {
  const { placements, lifetime } = history;

  if (placements.length === 0) {
    return (
      <div className={styles.panel}>
        {/*
          The lifetime figures come first and outlive the empty state: a founder with no
          placement can still have been visited from the Founding wall (`D-018`), and the
          callout below is about *placements* rather than about clicks.
        */}
        <LifetimeClicksCard lifetime={lifetime} note={history.clicks.message} />
        <Callout title="No placement of yours has run yet">
          A position you win, a listing you buy and a day your deal is carried forward all
          appear here once that display day has begun. What you hold for tomorrow is on the
          auction panel above, not here.
        </Callout>
      </div>
    );
  }

  // A plain sum of the rows on screen, labelled as exactly that. It is the same qualified
  // count core sent for each row rather than a second measurement, so it cannot disagree
  // with the public lists; and it is one number, not an analytics surface (canon § 18).
  const totalClicks = placements.reduce((total, row) => total + row.qualifiedClicks, 0);
  const upTo = formatInstantDate(history.serverTime);

  return (
    <div className={styles.panel}>
      <Card tone="lead" className={styles.summary}>
        <p className={styles.headline}>
          <span className={`${styles.total} figure`}>{String(totalClicks)}</span>
          <span className={styles.totalLabel}>
            qualified outbound clicks across the{" "}
            <span className="figure">{String(placements.length)}</span>{" "}
            {placements.length === 1 ? "placement" : "placements"} below
          </span>
        </p>
        {/* Canon § 1, in core's own words: the sentence that bounds what this number is
            travels with it, exactly as the non-cash terms travel with a credit balance. */}
        <p className={styles.note}>{history.clicks.message}</p>
        <p className={styles.scope}>
          Dealbid records a placement once its display day has begun, so this covers every day
          up to and including {upTo ?? history.serverTime} ({history.timeZone}). What you hold
          for tomorrow is on the auction panel above.
        </p>
      </Card>

      <LifetimeClicksCard lifetime={lifetime} note={null} />

      <ul className={styles.rows} role="list">
        {placements.map((placement) => (
          <li key={placement.placementId}>
            <PlacementRow placement={placement} timeZone={history.timeZone} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Every click a founder's deals have ever taken, split by what it counts for.
 *
 * **The two figures are never added together**, and nothing on this card offers a total.
 * `D-018` point 6 turns on that: the weekly and monthly lists count one of them and not the
 * other, so a merged number placed beside a founder's ranking effort would overstate it by
 * however many wall visits they had. Each figure carries the sentence that says what it
 * counts for, on its own line, rather than relying on a heading a reader may skip.
 *
 * **Neither is a promise.** Canon § 1 declines to promise sales, leads or an impression count
 * in the product thesis, so these are described as clicks and visits and as nothing else —
 * the same refusal `history.clicks.message` states in core's own words, which is why that
 * sentence is carried here when no other card on the panel is carrying it.
 *
 * Absent figures render nothing at all. `parseLifetimeClicks` answers `null` for a body it
 * cannot read, and a card that guessed — or showed one half — would be a claim about a
 * founder's record that this app cannot support.
 */
function LifetimeClicksCard({
  lifetime,
  note,
}: {
  lifetime: LifetimeClicks | null;
  /** Canon § 1's qualifying sentence, when this card is the only one on the panel. */
  note: string | null;
}) {
  if (lifetime === null) return null;

  return (
    <Card className={styles.lifetime}>
      <h3 className={styles.lifetimeTitle}>Clicks through to your deals, all time</h3>
      <dl className={styles.lifetimeFigures}>
        <div className={styles.lifetimeFigure}>
          <dt className={`${styles.lifetimeNumber} figure`}>
            {String(lifetime.rankingClicks)}
          </dt>
          <dd className={styles.lifetimeLabel}>
            qualified clicks that count toward the weekly and monthly Top deals lists
          </dd>
        </div>
        <div className={styles.lifetimeFigure}>
          <dt className={`${styles.lifetimeNumber} figure`}>
            {String(lifetime.foundingWallClicks)}
          </dt>
          <dd className={styles.lifetimeLabel}>
            visits from the Founding Deals wall on the homepage. Real buyers, and they count
            toward no Dealbid list
          </dd>
        </div>
      </dl>
      {note === null ? null : <p className={styles.note}>{note}</p>}
    </Card>
  );
}

/**
 * One placement, on one display day.
 *
 * The rank mark is drawn only when there **is** a rank. Every other panel in this app fills
 * an empty mark slot with an em dash, and that is right where the dash means "no bid yet" —
 * here it would sit in a column of positions and read as one. A row outside the Top 10 says
 * so in words instead, which is the same reason the winning amount is absent rather than
 * shown as `$0`: canon § 10 gives an Honorable Mention no position and canon §§ 6 and 9 make
 * a rollover and a founding placement cost nothing additional.
 */
function PlacementRow({
  placement,
  timeZone,
}: {
  placement: SellerPlacement;
  /** Core's own statement of the zone these dates are in (canon § 8). Always `UTC`. */
  timeZone: string;
}) {
  const copy = describePlacementSource(placement.source);
  const day = formatDisplayDateLong(placement.displayDate);
  const ranked = placement.rank !== null;

  return (
    <Card as="article" className={ranked ? styles.row : `${styles.row} ${styles.unranked}`}>
      <div className={styles.head}>
        {ranked ? (
          <p className={styles.mark} aria-hidden="true">
            #{String(placement.rank)}
          </p>
        ) : (
          // The mark slot is kept, empty, so the product names still line up down the
          // column. It carries no glyph: an unranked row has nothing to put here.
          <p className={`${styles.mark} ${styles.noMark}`} aria-hidden="true" />
        )}

        <div className={styles.names}>
          <p className={styles.day}>
            {day} · {timeZone}
          </p>
          <h3 className={styles.product}>{placement.deal.productName}</h3>
          <p className={styles.source}>
            <span className={styles.label}>{copy.label}</span>
            {ranked ? (
              <>
                {" · "}
                position <span className="figure">{String(placement.rank)}</span> in the Top 10
                that day
              </>
            ) : (
              <>
                {" · "}
                outside the Top 10, so it had no position
              </>
            )}
            {placement.winningBidCents === null ? null : (
              <>
                {" · "}
                winning bid{" "}
                <span className="figure">{formatMoney(placement.winningBidCents)}</span>
              </>
            )}
          </p>
        </div>

        <p className={styles.clicks}>
          <span className={`${styles.clickCount} figure`}>
            {String(placement.qualifiedClicks)}
          </span>
          <span className={styles.clickLabel}>
            qualified clicks
            <VisuallyHidden>
              {" "}
              — qualified outbound clicks on {day}, not sales or guaranteed impressions
            </VisuallyHidden>
          </span>
        </p>
      </div>

      <p className={styles.why}>{copy.note}</p>
    </Card>
  );
}
